import { draftSenderPersonaPlaceholder } from "owa-locstrings/lib/strings/draftsenderpersonaplaceholder.locstring.json";
import { flaggedFilter } from "owa-locstrings/lib/strings/flaggedfilter.locstring.json";
import { getRelativeDateMessage } from "owa-observable-datetime";
import { getItemsOrConversationsSelectedText, getComputedSelectedRowKeysSizeForTable } from "owa-mail-list-store";
import { formatRelativeDateForMailListRow } from "owa-mail-list-item-shared";
import { pinnedGroupHeader } from "owa-locstrings/lib/strings/pinnedgroupheader.locstring.json";
import { unreadFilter } from "owa-locstrings/lib/strings/unreadfilter.locstring.json";
import loc, { format } from "owa-localize";
import PropertyIcons from "owa-mail-list-actions/lib/utils/conversationProperty/PropertyIcons";
import type { TableView } from "owa-mail-list-store";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import { repliedLabel, mentionedLabel, forwardedLabel, } from "owa-locstrings/lib/strings/propertyiconlabels.locstring.json";
import { meetingTentative, meetingDeclined, meetingCanceled, meetingAccepted, meeting, meetingStartsAt, important, hasAttachments, conversationExpanded, conversationCollapsed, copilotHighPriority, copilotLowPriority, externalSenderLabel, } from "../components/MailListItemContainer.locstring.json";
import getCopilotPrioritizeSettings from "owa-mail-copilot-settings/lib/selectors/getCopilotPrioritizeSettings";
import type { MailboxInfo } from "owa-client-types";
import { isPrioritizeOnDeliveryEnabled, isPrioritizeOnDemandOn } from "owa-copilot-settings-utils";

export default function getAriaLabelForMailListItemRow(tableView: TableView | undefined, tableViewId: string, shouldRowBeFocused: boolean, canRowBeExpanded: boolean, isRowExpanded: boolean, unreadCount: number, importance: string | undefined, hasAttachment: boolean, isFlagged: boolean, isPinned: boolean, subject: string | undefined, sendersOrRecipients: string | undefined, lastDeliveryTimestamp: string | undefined, thirdLineText: string, showDraftsIndicator: boolean, itemClassIcon: PropertyIcons | undefined, effectiveMentioned: boolean, returnTime: string | null, meetingStartTime: string | undefined, copilotInboxScore: number | undefined, mailboxInfo: MailboxInfo | undefined, shouldShowSenderOnTop: boolean, shouldShowExternalLabel: boolean): string {
    const labelArray = [];
    
    if (unreadCount > 0) {
        labelArray.push(loc(unreadFilter));
    }
    
    if (canRowBeExpanded) {
        if (isRowExpanded) {
            labelArray.push(loc(conversationExpanded));
        }
        else {
            labelArray.push(loc(conversationCollapsed));
        }
    }
    
    if (importance === "High") {
        labelArray.push(loc(important));
    }
    
    if (hasAttachment) {
        labelArray.push(loc(hasAttachments));
    }
    
    if (isFlagged) {
        labelArray.push(loc(flaggedFilter));
    }
    
    if (isPinned) {
        labelArray.push(loc(pinnedGroupHeader));
    }
    
    if (itemClassIcon === PropertyIcons.MailListViewReplied) {
        labelArray.push(loc(repliedLabel));
    }
    
    if (itemClassIcon === PropertyIcons.MailListViewForwarded) {
        labelArray.push(loc(forwardedLabel));
    }
    
    if (effectiveMentioned) {
        labelArray.push(loc(mentionedLabel));
    }
    
    if (copilotInboxScore && copilotInboxScore > 0 && mailboxInfo) {
        const { applyLowPriorityLabel, prioritizationEnabled } = getCopilotPrioritizeSettings(mailboxInfo);
        
        if ((prioritizationEnabled && isPrioritizeOnDeliveryEnabled(mailboxInfo)) ||
            isPrioritizeOnDemandOn(mailboxInfo)) {
            const isLowPriority = applyLowPriorityLabel && copilotInboxScore < 3;
            const isHighPriority = copilotInboxScore > 3;
            
            if (isLowPriority) {
                labelArray.push(loc(copilotLowPriority));
            }
            else if (isHighPriority) {
                labelArray.push(loc(copilotHighPriority));
            }
        }
    }
    
    // Add a label for item class icon (meetings)
    const itemClassIconAriaLabel = itemClassIcon && getItemClassIconAriaLabel(itemClassIcon);
    if (itemClassIconAriaLabel) {
        labelArray.push(itemClassIconAriaLabel);
    }
    
    // if this is a meeting add start time, if available
    if (meetingStartTime) {
        try {
            labelArray.push(format(loc(meetingStartsAt), getRelativeDateMessage(meetingStartTime)));
        }
        catch {
            // If there is an error, we don't want to crash the app.
            // We can safely ignore the error and not add the date to the label.
        }
    }
    
    if (shouldShowExternalLabel) {
        labelArray.push(loc(externalSenderLabel));
    }
    
    if (showDraftsIndicator) {
        labelArray.push(loc(draftSenderPersonaPlaceholder));
    }
    
    if (shouldShowSenderOnTop) {
        labelArray.push(sendersOrRecipients, subject);
    }
    else {
        labelArray.push(subject, sendersOrRecipients);
    }
    
    // to avoid confusion - if meeting start timestamp is available we shouldn't add last delivery timestamp
    if (!returnTime && !meetingStartTime && lastDeliveryTimestamp) {
        try {
            const formattedLastDeliveryTimestamp: string = formatRelativeDateForMailListRow(lastDeliveryTimestamp).displayText;
            labelArray.push(formattedLastDeliveryTimestamp);
        }
        catch {
            // If there is an error, we don't want to crash the app.
            // We can safely ignore the error and not add the date to the label.
        }
    }
    
    // Only push preview text if it is not null (null when preview text off setting is on)
    if (thirdLineText) {
        labelArray.push(thirdLineText);
    }
    
    if (shouldRowBeFocused &&
        (isGlobalFeatureEnabled("fwk-ml-reduce-render-scope")
            ? getComputedSelectedRowKeysSizeForTable(tableViewId) !== 1
            : tableView?.selectedRowKeys.size !== 1)) {
        labelArray.push(getItemsOrConversationsSelectedText(tableViewId));
    }
    
    return labelArray.join(" "); // convert into string
}

const getItemClassIconAriaLabel = (icon: PropertyIcons): string | null => {
    switch (icon) {
        case PropertyIcons.MailListViewAppointmentItem:
            return loc(meeting);
        case PropertyIcons.MailListViewAppointmentAccepted:
            return loc(meetingAccepted);
        case PropertyIcons.MailListViewAppointmentDeclined:
            return loc(meetingDeclined);
        case PropertyIcons.MailListViewAppointmentTentative:
            return loc(meetingTentative);
        case PropertyIcons.MailListViewAppointmentCanceled:
            return loc(meetingCanceled);
        default:
            return null;
    }
};
